// SPDX-License-Identifier: AGPL-3.0-or-later

import {GuildBadge} from '@app/features/guild/components/GuildBadge';
import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import {ServerTagBadge} from '@app/features/guild/components/ServerTagBadge';
import styles from '@app/features/guild/components/popouts/GuildShowcasePopout.module.css';
import GuildCount from '@app/features/guild/state/GuildCount';
import Guilds from '@app/features/guild/state/Guilds';
import {formatNumber} from '@app/features/i18n/utils/IntlCache';
import Navigation from '@app/features/navigation/state/Navigation';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import {ArrowRightIcon, XIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

interface GuildShowcasePopoutProps {
	guildId: string;
	onClose: () => void;
}

export const GuildShowcasePopout: React.FC<GuildShowcasePopoutProps> = observer(({guildId, onClose}) => {
	const guild = Guilds.getGuild(guildId);
	const currentLocale = getCurrentLocale();

	useEffect(() => {
		if (guildId) {
			GuildCount.requestCounts(guildId);
		}
	}, [guildId]);

	if (!guild) {
		return null;
	}

	const counts = GuildCount.getCounts(guildId);
	const memberCount = counts?.memberCount ?? guild.memberCount ?? 0;
	const onlineCount = counts?.onlineCount ?? null;

	const bannerUrl = guild.banner
		? AvatarUtils.getGuildBannerURL({id: guild.id, banner: guild.banner}, true)
		: null;


	const handleNavigate = () => {
		Navigation.navigateToGuild(guild.id);
		onClose();
	};

	return (
		<div className={styles.container} data-flx="guild.guild-showcase-popout">
			{/* Banner / Header */}
			<div
				className={clsx(styles.banner, !bannerUrl && styles.bannerGradient)}
				style={bannerUrl ? {backgroundImage: `url(${bannerUrl})`} : undefined}
			>
				<button
					type="button"
					className={styles.closeButton}
					onClick={onClose}
					aria-label="Close"
				>
					<XIcon size={16} weight="bold" />
				</button>
			</div>

			{/* Server Profile Body */}
			<div className={styles.body}>
				{/* Overlapping Guild Icon */}
				<div className={styles.iconWrapper}>
					<GuildIcon
						id={guild.id}
						name={guild.name}
						icon={guild.icon}
						sizePx={56}
						className={styles.guildIcon}
					/>
				</div>

				{/* Title and Badges */}
				<div className={styles.headerInfo}>
					<div className={styles.titleRow}>
						<h3 className={styles.guildName} title={guild.name}>
							{guild.name}
						</h3>
						<GuildBadge features={guild.features} badges={guild.badges} />
					</div>

					{(guild.tag || (guild.tagBadge && guild.tagBadge !== 'none')) && (
						<div className={styles.tagWrapper}>
							<ServerTagBadge
								tag={guild.tag || ''}
								badge={guild.tagBadge}
								color={guild.tagColor}
								interactive={false}
							/>
						</div>
					)}
				</div>

				{/* Member Stats */}
				<div className={styles.statsRow}>
					{onlineCount !== null && (
						<div className={styles.statItem}>
							<span className={styles.onlineDot} />
							<span className={styles.statValue}>
								{formatNumber(onlineCount, currentLocale)}
							</span>
							<span className={styles.statLabel}>Online</span>
						</div>
					)}
					<div className={styles.statItem}>
						<span className={styles.memberDot} />
						<span className={styles.statValue}>
							{formatNumber(memberCount, currentLocale)}
						</span>
						<span className={styles.statLabel}>Members</span>
					</div>
				</div>

				{/* Action CTA */}
				<div className={styles.actionRow}>
					<button
						type="button"
						className={styles.viewServerButton}
						onClick={handleNavigate}
					>
						<span>View Server</span>
						<ArrowRightIcon size={16} weight="bold" />
					</button>
				</div>
			</div>
		</div>
	);
});

export default GuildShowcasePopout;
